import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType, TextFieldWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { HelpService } from '~src/commonInfra/services/HelpService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { SessionUserSelectors } from '../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '../../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { SiteRolePickerMenu } from '../../components/siteRolePicker/SiteRolePickerMenu';
import { SiteRoleOption, SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { SiteRoles } from '../../users/serverApi/types/ISiteRole';
import { GrantLicenseModes } from '../serverApi/requests/SyncActiveDirectoryGroupRequest';
import {
  dialogBodyStyles,
  dialogElementStyles,
  externalUserCheckboxStyles,
  externalUserLabelStyles,
  externalUserTextLinkStyles,
  externalUserWarningStyles,
  groupNameFieldStyles,
  labelStyles,
  outerCheckboxStyles,
  siteRolePickerMenuStyles,
  textFieldStyles} from './styles/CreateGroupDialogStyles';


const GRANT_LICENSE_ON_LOGIN_CHECKBOX_TEST_ID = 'license-upon-login';
const EXTERNAL_USER_ENABLED_CHECKBOX_TEST_ID = 'external-user-enabled';
const NAME_GROUP_TEXT_FIELD_TEST_ID = 'confirm-action-dialog-input-text-box';

const ZERO_WIDTH_SPACE = '\u200B';

export const CHARACTER_LIMIT = 255;

export type CreateGroupDialogProps = {}

export type CreateGroupDialogState = {
  groupNameErrorMessage: string;
  groupNameText: string;
  grantLicenseMode: GrantLicenseModes;
  isGroupNameTextValid: boolean;
  minimumSiteRole: string;
  externalUserEnabled: boolean;
}

export type CreateGroupActionDialogProps = ReactActionDialogBodyProps<CreateGroupDialogProps, CreateGroupDialogState>;

export function CreateGroupDialogBody(props: CreateGroupActionDialogProps): JSX.Element {
  const {
    groupNameErrorMessage,
    groupNameText,
    grantLicenseMode,
    isGroupNameTextValid,
    minimumSiteRole,
    externalUserEnabled
  } = props.dialogState;
  const isExternalUserEnabledForSite = getSessionManagementServiceInstance().isExternalUserEnabledForSite();

  return <>
    <div style={dialogBodyStyles}>
      <div style={dialogElementStyles}>
        <DialogLabel message={Messages.groupName()} labelStyles={labelStyles}/>
        <GroupNameField
          groupNameErrorMessage={groupNameErrorMessage}
          groupNameText={groupNameText}
          isGroupNameTextValid={isGroupNameTextValid}
          doAction={props.doAction}
          updateState={props.updateDialogState}/>
      </div>
      {isExternalUserEnabledForSite &&
      <div style={dialogElementStyles}>
        <DialogLabel message={Messages.groupEphemeralUsers()} labelStyles={externalUserLabelStyles}/>
          <div>
            <AllowExternalUserCheckBox
              externalUserEnabled={externalUserEnabled}
              updateState={props.updateDialogState}/>
          </div>
      </div>}
      <div style={dialogElementStyles}>
        <DialogLabel message={Messages.groupLicensedUsers()} labelStyles={labelStyles}/>
          <div>
            <GrantLicenseModeOnLoginCheckBox
              grantLicenseMode={grantLicenseMode}
              updateState={props.updateDialogState}/>
            <SiteRolePickerMenuWithInfo
              grantLicenseMode={grantLicenseMode}
              minimumSiteRole={minimumSiteRole}
              updateState={props.updateDialogState}/>
          </div>
      </div>
    </div>
  </>;
}

type GroupNameFieldProps = {
  groupNameErrorMessage: string;
  groupNameText: string;
  isGroupNameTextValid: boolean;
  doAction: () => void;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function GroupNameField(props: GroupNameFieldProps): JSX.Element {
  const {
    groupNameErrorMessage,
    groupNameText,
    isGroupNameTextValid,
    doAction
  } = props;

  const onTextFieldClear = () => props.updateState({ groupNameErrorMessage: undefined, groupNameText: '', isGroupNameTextValid: true });
  const onTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const isValid = name.length <= CHARACTER_LIMIT ? true : false;
    props.updateState({
      groupNameErrorMessage: isValid ? undefined : Messages.characterLimitExceeded({
        COUNT_LOC: Formatters.formatCount(name.length),
        LIMIT_LOC: Formatters.formatCount(CHARACTER_LIMIT)
      }),
      groupNameText: name,
      isGroupNameTextValid: isValid,
    });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      doAction();
    } else if (e.key === 'Escape') {
      onTextFieldClear();
    }
  };

  return <TextFieldWidget
    className={textFieldStyles}
    kind='outline'
    label={''}
    aria-label={Messages.groupName()}
    onChange={onTextFieldChange}
    onClear={onTextFieldClear}
    onKeyDown={onKeyDown}
    message={groupNameErrorMessage ? groupNameErrorMessage : ZERO_WIDTH_SPACE}
    spellCheck={false}
    testId={NAME_GROUP_TEXT_FIELD_TEST_ID}
    valid={isGroupNameTextValid ? undefined : false}
    value={groupNameText}
    style={groupNameFieldStyles}
    placeholder={Messages.groupNameShadowText()}
  />;
}

type GrantLicenseModeOnLoginCheckBoxProps = {
  grantLicenseMode: GrantLicenseModes;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function GrantLicenseModeOnLoginCheckBox(props: GrantLicenseModeOnLoginCheckBoxProps): JSX.Element {
  const handleCheckboxChange = (checked: boolean) => {
    const grantLicenseMode = checked ? GrantLicenseModes.ON_LOGIN : undefined;
    props.updateState({ grantLicenseMode });
  };

  return <CheckBoxWithLabelWidget
    checked={props.grantLicenseMode === GrantLicenseModes.ON_LOGIN}
    handleChange={handleCheckboxChange}
    testId={GRANT_LICENSE_ON_LOGIN_CHECKBOX_TEST_ID}
    label={Messages.grantLicenseUponLoginCheckboxLabel()}
    disabled={!SessionUserSelectors.canManageUsers(getAppStore().getState())}
    style={outerCheckboxStyles}
  />;
}

type SiteRolePickerMenuWithInfoProps = {
  grantLicenseMode: GrantLicenseModes;
  minimumSiteRole: string;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function SiteRolePickerMenuWithInfo(props: SiteRolePickerMenuWithInfoProps): JSX.Element {
  const roleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

  const disabledRoleOptions = (roleOptions: SiteRoleOption[]) =>
    _.filter(roleOptions, (option: SiteRoleOption) => option.role === SiteRoles.Unlicensed);

  const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
    const minimumSiteRole = roleOption.role;
    const grantLicenseMode = minimumSiteRole === SiteRoles.Unlicensed ? undefined : props.grantLicenseMode;
    props.updateState({ grantLicenseMode, minimumSiteRole });
  };

  const getRoleOption = (siteRole: string) => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  };

  return <div style={siteRolePickerMenuStyles}>
    <SiteRolePickerMenu
      disabledRoleOptions={disabledRoleOptions(roleOptions)}
      dropdownLabel={Messages.minimumSiteRole()}
      getSiteRoleOptionBySiteRole={getRoleOption}
      isDisabled={props.grantLicenseMode !== GrantLicenseModes.ON_LOGIN}
      message={ZERO_WIDTH_SPACE}
      onOptionSelected={onSiteRoleSelected}
      selectedRole={getRoleOption(props.minimumSiteRole)}
      showSiteRoleInfoLink={true}
      siteRoleOptions={roleOptions}
    />
  </div>;
}

type DialogLabelStyleProps = {
  message: string;
  labelStyles: React.CSSProperties;
}

function DialogLabel(props: DialogLabelStyleProps): JSX.Element {
  return <div style={props.labelStyles}><span>{props.message}</span></div>;
}

type AllowExternalUserCheckBoxProps = {
  externalUserEnabled: boolean;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

export function AllowExternalUserCheckBox(props: AllowExternalUserCheckBoxProps): JSX.Element {
  const handleCheckboxChange = (checked: boolean) => {
    const externalUserEnabled = checked ? true : undefined;
    props.updateState({ externalUserEnabled });
  };
  return (
      <div>
        <CheckBoxWithLabelWidget
          checked={props.externalUserEnabled}
          handleChange={handleCheckboxChange}
          testId={EXTERNAL_USER_ENABLED_CHECKBOX_TEST_ID}
          label={Messages.groupEphemeralUsersCheckBoxLabel()}
          disabled={!SessionUserSelectors.canManageUsers(getAppStore().getState())}
          style={externalUserCheckboxStyles} />
          <ExternalUserTextLink/>
          {props.externalUserEnabled &&
                      <CompactInlineAlertWidget
                          children={Messages.groupEphemeralUserWarning()}
                          type={InlineAlertType.Warning}
                          testId='edit-external-user-enable-warning'
                          style={externalUserWarningStyles}/>
          }
      </div>
  );
}

function ExternalUserTextLink(): JSX.Element {
  return <div style={externalUserTextLinkStyles}>
    <span>{Messages.groupEphemeralUsersDetailsLabel()}</span>
    &nbsp;
    <TextLinkWidget
      testId={'external-user-learn-more'}
      target={'_blank'}
      href={HelpService.getHelpUrlByCategory('groups-ephemeral-users')}
      kind={TextLinkKind.Standalone}>
      {Messages.learnMore()}
    </TextLinkWidget>
  </div>;
}